import { useState, useMemo } from "react";
import { DRIVERS, STATUSES, SPRINTS, S, VALUE_BADGE, formatDate, scoreStyle, scoreLabel, CONFIDENCE_LEVELS, INITIATIVE_STAGES, PRIMARY_SURFACES } from "./constants";
import { DriverTag, StatusBadge, ConfluenceIcon, DeleteButton } from "./components";

// Columns sorted by raw numeric comparison (everything else is string or $-value)
const NUMERIC_SORT_KEYS = new Set(["score", "sprints", "sprint", "points"]);

export default function ListView({ projects, backlog, completed, onEdit, onRemove, onMarkComplete, onRestore, canEdit, lanes = [], scoringMethod = "score",
  listFilter, onListFilterChange, onBulkUpdate, onBulkMoveToBacklog }) {
  const [sortKey, setSortKey]       = useState("score");
  const [sortDir, setSortDir]       = useState("desc");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchText, setSearchText] = useState("");

  const filterLane   = listFilter?.lane   ?? "all";
  const filterDriver = listFilter?.driver ?? "all";
  const filterStatus = listFilter?.status ?? "all";
  const filterRisk   = listFilter?.risk   ?? "all";
  const filterStage  = listFilter?.stage  ?? "all";
  const setFilter    = (key, val) => onListFilterChange?.({ ...(listFilter || {}), [key]: val });
  const setFilterLane   = (v) => setFilter("lane",   v);
  const setFilterDriver = (v) => setFilter("driver", v);
  const setFilterStatus = (v) => setFilter("status", v);
  const setFilterRisk   = (v) => setFilter("risk",   v);
  const setFilterStage  = (v) => setFilter("stage",  v);

  const allItems = useMemo(() => [
    ...projects.map((p)   => ({ ...p, status: p.status || "scheduled" })),
    ...backlog.map((p)    => ({ ...p, status:"backlog",   sprint: undefined })),
    ...completed.map((p)  => ({ ...p, status:"completed" })),
  ], [projects, backlog, completed]);

  const trimmedSearch = searchText.trim();
  const hasActiveFilters = filterLane !== "all" || filterDriver !== "all" || filterStatus !== "all" || filterRisk !== "all" || filterStage !== "all" || trimmedSearch !== "";
  const clearFilters = () => { onListFilterChange?.({ lane:"all", driver:"all", status:"all", risk:"all", stage:"all" }); setSearchText(""); };

  const filtered = allItems
    .filter((p) => !trimmedSearch || p.name.toLowerCase().includes(trimmedSearch.toLowerCase()))
    .filter((p) => filterLane   === "all" || p.lane   === filterLane)
    .filter((p) => filterDriver === "all" || p.driver === filterDriver)
    .filter((p) => filterStatus === "all" || p.status === filterStatus)
    .filter((p) => {
      if (filterRisk === "all")        return true;
      if (filterRisk === "atrisk")     return p.atRisk;
      if (filterRisk === "legal")      return p.legalRisk;
      if (filterRisk === "appstore")   return p.appStoreRisk;
      if (filterRisk === "blocker")    return !!p.externalDependency?.trim();
      if (filterRisk === "partner")    return !!p.partnerDependencies?.trim();
      if (filterRisk === "lowconf")    return p.confidenceLevel === "low" || p.confidenceLevel === "medium";
      return true;
    })
    .filter((p) => filterStage === "all" || p.initiativeStage === filterStage)
    .sort((a, b) => {
      let cmp;
      if (sortKey === "value") {
        // "$800K" / "$1.2M" → compare by extracted numeric magnitude, not lexically
        const num = (v) => parseInt((v || "").replace(/\D/g, ""), 10) || 0;
        cmp = num(a.value) - num(b.value);
      } else if (NUMERIC_SORT_KEYS.has(sortKey)) {
        cmp = (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
      } else {
        // string columns (name, lane, driver) — alphabetical, case-insensitive
        cmp = (a[sortKey] ?? "").toString().toLowerCase()
          .localeCompare((b[sortKey] ?? "").toString().toLowerCase());
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

  const toggleSelect   = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected    = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const toggleAll      = () => setSelectedIds(allSelected ? new Set() : new Set(filtered.map((p) => p.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const execBulk       = (changes) => { onBulkUpdate?.(Array.from(selectedIds), changes); clearSelection(); };
  const execMoveToBacklog = () => { onBulkMoveToBacklog?.(Array.from(selectedIds)); clearSelection(); };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d==="asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const SortIcon = ({ k }) => sortKey===k
    ? <span style={{ marginLeft:4, opacity:0.6 }}>{sortDir==="asc" ? "↑" : "↓"}</span>
    : <span style={{ marginLeft:4, opacity:0.2 }}>↕</span>;

  const completedCount = allItems.filter((p) => p.status === "completed").length;

  const COL = [
    { key:"name",    label:"Project",       w:"20%" },
    { key:"lane",    label:"Team",          w:"9%"  },
    { key:"driver",  label:"Driver",        w:"12%" },
    { key:"score",   label: scoringMethod === "rice" ? "RICE" : "Score", w:"6%"  },
    { key:"value",   label:"$ Value",       w:"6%"  },
    { key:"sprints", label:"Sprints",       w:"5%"  },
    { key:"sprint",  label:"Starts / Target", w:"10%" },
    { key:"status",  label:"Status / Conf.", w:"13%", noSort:true },
    { key:"biz",     label:"Business Case", w:"13%", noSort:true },
  ];

  return (
    <div style={{ flex:1, overflow:"auto", paddingBottom:24 }}>
      {completedCount > 0 && (
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 24px", background:"var(--completed-bg)", borderBottom:"1px solid var(--completed-border)" }}>
          <span style={{ fontSize:13 }}>✅</span>
          <span style={{ fontSize:12, fontWeight:700, color:"#0369A1" }}>
            {completedCount} project{completedCount!==1?"s":""} completed
          </span>
          <button onClick={() => setFilterStatus(filterStatus==="completed" ? "all" : "completed")}
            style={{ fontSize:11, color:"#0369A1", background:"transparent", border:"1px solid #7DD3FC",
              borderRadius:5, padding:"2px 8px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>
            {filterStatus==="completed" ? "Show all" : "View completed"}
          </button>
        </div>
      )}

      <div style={{ display:"flex", gap:8, padding:"12px 24px", borderBottom:"1px solid var(--border)", background:"var(--bg-header)", alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ position:"relative", flexShrink:0 }}>
          <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"var(--text-muted)", pointerEvents:"none" }} aria-hidden="true">🔍</span>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search projects…"
            aria-label="Search projects"
            style={{ ...S.filterSelect, paddingLeft:26, width:160 }}
          />
        </div>
        <span style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Filter</span>
        <select value={filterLane}   onChange={(e) => setFilterLane(e.target.value)}   style={S.filterSelect} aria-label="Filter by team">
          <option value="all">All Teams</option>
          {lanes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
        <select value={filterDriver} onChange={(e) => setFilterDriver(e.target.value)} style={S.filterSelect} aria-label="Filter by driver">
          <option value="all">All Drivers</option>
          {Object.entries(DRIVERS).map(([k,d]) => <option key={k} value={k}>{d.label}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={S.filterSelect} aria-label="Filter by status">
          <option value="all">All Statuses</option>
          {Object.entries(STATUSES).map(([k,s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <select value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)} style={S.filterSelect} aria-label="Filter by risk">
          <option value="all">All Risks</option>
          <option value="atrisk">🚩 At Risk</option>
          <option value="legal">⚖️ Legal Risk</option>
          <option value="appstore">📱 App Store Risk</option>
          <option value="blocker">🔗 Has Ext. Blocker</option>
          <option value="partner">🤝 Partner-Gated</option>
          <option value="lowconf">⚠️ Low/Med Confidence</option>
        </select>
        <select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} style={S.filterSelect} aria-label="Filter by initiative stage">
          <option value="all">All Stages</option>
          {Object.entries(INITIATIVE_STAGES).map(([k,s]) => <option key={k} value={k}>{s.icon} {s.label}</option>)}
        </select>
        {hasActiveFilters && (
          <button onClick={clearFilters} style={{ ...S.btnClearFilter, whiteSpace:"nowrap", flexShrink:0 }}>✕ Clear</button>
        )}
        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--text-muted)" }}>{filtered.length} project{filtered.length!==1?"s":""}</span>
      </div>

      <div style={{ padding:"0 24px" }}>
        <div style={{ display:"flex", borderBottom:"2px solid var(--border)", padding:"10px 0 8px", alignItems:"center" }}>
          {canEdit && onBulkUpdate && (
            <div style={{ width:28, flexShrink:0, paddingRight:4 }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll}
                title={allSelected ? "Deselect all" : "Select all"}
                style={{ cursor:"pointer", accentColor:"var(--accent)" }} />
            </div>
          )}
          {COL.map((c) => (
            <div key={c.key} style={{ width:c.w, flexShrink:0, paddingRight:8 }}>
              <button disabled={c.noSort} onClick={() => !c.noSort && handleSort(c.key)} style={{
                background:"none", border:"none", padding:0, cursor:c.noSort?"default":"pointer",
                fontSize:10, fontWeight:800, color:"var(--text-secondary)", textTransform:"uppercase",
                letterSpacing:"0.06em", fontFamily:"inherit", display:"flex", alignItems:"center",
              }}>
                {c.label}{!c.noSort && <SortIcon k={c.key} />}
              </button>
            </div>
          ))}
          <div style={{ width:"6%", flexShrink:0 }} />
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"56px 0" }}>
            <div style={{ fontSize:28, marginBottom:10, opacity:0.25 }}>◫</div>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--text-secondary)", marginBottom:4, letterSpacing:"-0.01em" }}>No projects found</div>
            <div style={{ fontSize:12, color:"var(--text-muted)" }}>Try adjusting your filters above</div>
          </div>
        )}

        {filtered.map((p, rowIndex) => {
          const lane   = lanes.find((l) => l.id === p.lane);
          const rStyle = scoreStyle(p.score, scoringMethod);
          const sprint = p.sprint !== undefined ? SPRINTS[p.sprint] : null;
          const isDone = p.status === "completed";
          const isOdd  = rowIndex % 2 === 1;
          const conf   = p.confidenceLevel ? CONFIDENCE_LEVELS[p.confidenceLevel] : null;
          const stage  = p.initiativeStage ? INITIATIVE_STAGES[p.initiativeStage] : null;
          const surf   = p.primarySurface  ? PRIMARY_SURFACES[p.primarySurface]   : null;
          return (
            <div key={p.id} style={{
              display:"flex", alignItems:"center", borderBottom:"1px solid var(--border)",
              padding:"14px 0", transition:"background 0.12s",
              background: selectedIds.has(p.id) ? "var(--sprint-next-bg)" : isOdd ? "rgba(0,0,0,0.016)" : "transparent",
              opacity: isDone ? 0.65 : 1,
            }}
              onMouseEnter={(e) => { if (!selectedIds.has(p.id)) e.currentTarget.style.background="var(--bg-hover)"; }}
              onMouseLeave={(e) => { if (!selectedIds.has(p.id)) e.currentTarget.style.background = isOdd ? "rgba(0,0,0,0.016)" : "transparent"; }}>

              {canEdit && onBulkUpdate && (
                <div style={{ width:28, flexShrink:0, paddingRight:4 }}>
                  <input type="checkbox" checked={selectedIds.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor:"pointer", accentColor:"var(--accent)" }} />
                </div>
              )}

              <div style={{ width:"20%", flexShrink:0, paddingRight:8 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"var(--text-primary)", display:"flex", alignItems:"center", gap:4,
                  textDecoration: "none" }}>
                  {isDone && <span style={{ color:"#0369A1", fontSize:14 }}>✓</span>}
                  {VALUE_BADGE(p.value) && !isDone && <span>{VALUE_BADGE(p.value)}</span>}
                  {p.aiInitiative && !isDone && <span title="AI Initiative">🤖</span>}
                  {p.legalRisk    && !isDone && <span title="Legal risk">⚖️</span>}
                  {p.appStoreRisk && !isDone && <span title="App Store risk">📱</span>}
                  {p.name}
                </div>
                <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:2, display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
                  <span>{p.sprints} sprint{p.sprints!==1?"s":""} · {p.points} pts</span>
                  {surf && <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3,
                    background:surf.bg, color:surf.color, border:`1px solid ${surf.border}` }}>{surf.label}</span>}
                </div>
              </div>

              <div style={{ width:"9%", flexShrink:0, paddingRight:8 }}>
                <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:3,
                  background:lane?.color ? `${lane.color}18` : undefined, color:lane?.color, border:`1px solid ${lane?.color ? `${lane.color}40` : 'var(--border)'}`, whiteSpace:"nowrap" }}>
                  {lane?.label}
                </span>
              </div>

              <div style={{ width:"12%", flexShrink:0, paddingRight:8 }}><DriverTag driver={p.driver} /></div>

              <div style={{ width:"6%", flexShrink:0, paddingRight:8 }}>
                <span style={{ ...S.pill, background:rStyle.bg, color:rStyle.text, border:`1px solid ${rStyle.border}`, fontSize:11 }}>{scoreLabel(p.score, scoringMethod)}</span>
              </div>

              <div style={{ width:"6%", flexShrink:0, paddingRight:8 }}>
                <span style={{ fontSize:12, fontWeight:600, color:"var(--text-secondary)" }}>{p.value||"—"}</span>
              </div>

              <div style={{ width:"5%", flexShrink:0, paddingRight:8 }}>
                <span style={{ fontSize:12, color:"var(--text-secondary)" }}>{p.sprints}</span>
              </div>

              <div style={{ width:"10%", flexShrink:0, paddingRight:8 }}>
                {p.targetLaunchDate ? (
                  <span style={{ fontSize:11 }}>
                    <span style={{ fontSize:9, fontWeight:800, color:"#1A6BC4", display:"block", marginBottom:1 }}>TARGET</span>
                    <span style={{ fontWeight:700, color:"#1A6BC4" }}>
                      {new Date(p.targetLaunchDate + "T00:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric", year:"2-digit" })}
                    </span>
                  </span>
                ) : sprint ? (
                  <span style={{ fontSize:11, color:"var(--text-secondary)" }}>
                    <span style={{ fontWeight:700 }}>{sprint.label}</span><br/>
                    <span style={{ fontSize:10, color:"var(--text-muted)" }}>{formatDate(sprint.start)}</span>
                  </span>
                ) : <span style={{ fontSize:11, color:"#CBD5E1", fontStyle:"italic" }}>—</span>}
              </div>

              <div style={{ width:"13%", flexShrink:0, paddingRight:8, display:"flex", flexDirection:"column", gap:3, alignItems:"flex-start" }}>
                <StatusBadge status={p.status} />
                {conf && (
                  <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3,
                    background:conf.bg, color:conf.color, border:`1px solid ${conf.border}`, whiteSpace:"nowrap" }}>
                    {conf.label}
                  </span>
                )}
                {stage && (
                  <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3,
                    background:stage.bg, color:stage.color, border:`1px solid ${stage.border}`, whiteSpace:"nowrap" }}>
                    {stage.icon} {stage.label}
                  </span>
                )}
              </div>

              <div style={{ width:"13%", flexShrink:0, paddingRight:8 }}>
                {p.confluenceUrl ? (
                  <a href={p.confluenceUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600, color:"#2563EB",
                      textDecoration:"none", padding:"3px 8px", background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:5, whiteSpace:"nowrap" }}
                    onMouseEnter={(e) => e.currentTarget.style.background="#DBEAFE"}
                    onMouseLeave={(e) => e.currentTarget.style.background="#EFF6FF"}>
                    <ConfluenceIcon />View Doc
                  </a>
                ) : (
                  <button onClick={canEdit ? () => onEdit(p) : undefined} disabled={!canEdit} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600,
                    color: canEdit ? "var(--text-muted)" : "var(--border)", background:"transparent", border: canEdit ? "1px dashed var(--border)" : "1px dashed var(--border)", borderRadius:5,
                    padding:"3px 8px", cursor: canEdit ? "pointer" : "not-allowed", fontFamily:"inherit", whiteSpace:"nowrap" }}
                    onMouseEnter={(e) => { if (canEdit) { e.currentTarget.style.color="#2563EB"; e.currentTarget.style.borderColor="#93C5FD"; } }}
                    onMouseLeave={(e) => { if (canEdit) { e.currentTarget.style.color="var(--text-muted)"; e.currentTarget.style.borderColor="var(--border)"; } }}>
                    + Add link
                  </button>
                )}
              </div>

              <div style={{ width:"6%", flexShrink:0, display:"flex", gap:3, justifyContent:"flex-end" }}>
                {!isDone ? (
                  <button title="Mark complete" aria-label={`Mark ${p.name} as complete`}
                    onClick={canEdit ? () => onMarkComplete(p) : undefined} disabled={!canEdit}
                    style={{ ...S.iconBtn, color: canEdit ? "var(--color-success,#22C55E)" : "#CBD5E1", fontSize:13, cursor: canEdit ? "pointer" : "not-allowed" }}>✓</button>
                ) : (
                  <button title="Restore to scheduled" aria-label={`Restore ${p.name} to scheduled`}
                    onClick={canEdit ? () => onRestore(p) : undefined} disabled={!canEdit}
                    style={{ ...S.iconBtn, color: canEdit ? "var(--text-secondary)" : "var(--border)", fontSize:11, cursor: canEdit ? "pointer" : "not-allowed" }}>↺</button>
                )}
                <button title="Edit" aria-label={`Edit ${p.name}`}
                  onClick={canEdit ? () => onEdit(p) : undefined} disabled={!canEdit}
                  style={{ ...S.iconBtn, color: canEdit ? undefined : "#CBD5E1", cursor: canEdit ? "pointer" : "not-allowed" }}>✏️</button>
                {canEdit ? <DeleteButton onConfirm={() => onRemove(p)} ariaLabel={`Delete ${p.name}`} /> : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && canEdit && (
        <div style={{
          position:"sticky", bottom:0, left:0, right:0,
          background:"var(--bg-card)", borderTop:"2px solid var(--accent)",
          padding:"10px 24px", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
          boxShadow:"0 -4px 16px rgba(0,0,0,0.12)", zIndex:20,
        }}>
          <span style={{ fontSize:12, fontWeight:800, color:"var(--accent)", marginRight:4 }}>
            {selectedIds.size} selected
          </span>
          <button onClick={clearSelection} style={{ fontSize:11, fontWeight:600, color:"var(--text-muted)",
            background:"transparent", border:"1px solid var(--border)", borderRadius:5,
            padding:"3px 8px", cursor:"pointer", fontFamily:"inherit" }}>✕ Clear</button>
          <div style={{ width:1, height:20, background:"var(--border)" }} />
          <span style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)" }}>Set Lane:</span>
          <select onChange={(e) => { if (e.target.value) { execBulk({ lane: e.target.value }); e.target.value=""; }}}
            defaultValue="" aria-label="Set team for selected projects"
            style={{ ...S.filterSelect, fontSize:11 }}>
            <option value="">Team…</option>
            {lanes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          <span style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)" }}>Set Driver:</span>
          <select onChange={(e) => { if (e.target.value) { execBulk({ driver: e.target.value }); e.target.value=""; }}}
            defaultValue="" aria-label="Set driver for selected projects"
            style={{ ...S.filterSelect, fontSize:11 }}>
            <option value="">Driver…</option>
            {Object.entries(DRIVERS).map(([k,d]) => <option key={k} value={k}>{d.label}</option>)}
          </select>
          <span style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)" }}>Set Status:</span>
          <select onChange={(e) => { if (e.target.value) { execBulk({ status: e.target.value }); e.target.value=""; }}}
            defaultValue="" aria-label="Set status for selected projects"
            style={{ ...S.filterSelect, fontSize:11 }}>
            <option value="">Status…</option>
            {Object.entries(STATUSES).filter(([k]) => k !== "backlog" && k !== "completed").map(([k,s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
          {onBulkMoveToBacklog && (
            <button onClick={execMoveToBacklog} style={{
              fontSize:11, fontWeight:700, color:"#B45309",
              background:"#FEF3C7", border:"1px solid #FDE68A",
              borderRadius:5, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit",
            }}>→ Move to Backlog</button>
          )}
        </div>
      )}
    </div>
  );
}
